"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

import { NAV_ITEMS } from "@/components/layout/constants";
import { ROUTES } from "@/config/routes/paths";

import SideBarItem from "./SidebarItem";
import SidebarProfile from "./SidebarProfile";

interface SideBarProps {
  isOpenSideBar: boolean;
  onCloseSideBar: () => void;
}

const SideBar = ({ isOpenSideBar, onCloseSideBar }: SideBarProps) => {
  const pathname = usePathname();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [sidebarWidth, setSidebarWidth] = useState(200);

  const getInitialActiveItem = () => {
    if (pathname) return pathname.split("/")[1] || "client";
    return "client";
  };

  const [activeItem, setActiveItem] = useState<string>(getInitialActiveItem());

  useEffect(() => {
    if (pathname) {
      setActiveItem(pathname.split("/")[1] || "client");
    }
  }, [pathname]);

  // ResizeObserver로 사이드바 너비 동적 측정
  useEffect(() => {
    const updateWidth = () => {
      if (sidebarRef.current) {
        setSidebarWidth(sidebarRef.current.offsetWidth);
      }
    };
    const ro = new ResizeObserver(updateWidth);
    if (sidebarRef.current) {
      ro.observe(sidebarRef.current);
    }
    return () => ro.disconnect();
  }, []);

  // 바깥 클릭 닫기
  const handleCloseSideBar = useCallback(onCloseSideBar, [onCloseSideBar]);

  useEffect(() => {
    if (!isOpenSideBar) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if ((target as Element).closest?.("[data-sidebar-toggle]")) return;
      if (sidebarRef.current && !sidebarRef.current.contains(target)) {
        handleCloseSideBar();
      }
    };

    const id = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpenSideBar, handleCloseSideBar]);

  return (
    <div
      className="fixed top-(--header-h) z-[1200] transition-[left] duration-400 ease-in-out"
      style={{ left: isOpenSideBar ? "0px" : `-${sidebarWidth}px` }}
    >
      <div
        ref={sidebarRef}
        className="flex flex-col min-w-[200px] h-[calc(100vh-var(--header-h))] bg-primary-900 shadow-depth200 rounded-r-lg"
      >
        <div className="flex w-full h-24 justify-center items-center py-8">
          <Link href={ROUTES.CLIENT.LIST}>
            <Image src="/images/Sidebar/white.png" alt="UpSight" width={138} height={32} />
          </Link>
        </div>
        <SidebarProfile />

        <div className="flex flex-col flex-1 overflow-y-auto scrollbar-none">
          <div className="flex flex-col items-center gap-1 py-8">
            {NAV_ITEMS.map(({ key, label, href }) => (
              <SideBarItem
                key={key}
                href={href}
                label={label}
                isActive={activeItem === key}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SideBar;
